import MaterialTable, { MTableToolbar, MTableBody } from 'material-table';
import React, { useState, useEffect, useRef } from 'react';
import { Button } from 'react-bootstrap';

import MTableHeader from '../../components/material-table-overrides/MTableHeader';
import MTableFilterRow from '../../components/material-table-overrides/MTableFilterRow';
import TooltippedElement from '../../components/common/TooltippedElement';
import './P2p.less';

import { mapDtoToTable } from '../../dto/p2p';
import { styledTableHeading, tableIcons } from '../../helpers';
import { localization, modifyDefaultOptions } from '../../helpers/table';
import useAPI from '../../hooks/useAPI';
import { getFooter, getHeader, getYearRelatedColumns } from './TableHelper';
import ToolbarWrapper from '../../components/table/ToolbarWrapper';

const DEFAULT_YEAR = Number(DEFAULT_YEAR_WEBPACK);

export default function P2p() {
  const [strategies, setStrategies] = useState([]);
  const [filteredStrategies, setFilteredStrategies] = useState({})
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [filterYears, setFilterYears] = useState([DEFAULT_YEAR]);
  const [filterFunding, setFilterFunding] = useState(['base', 'special']);
  const [allRowsExpanded, setAllRowsExpanded] = useState(false);

  const api = useAPI();

  const updateStrategies = (strategies) => { 
    setFilteredStrategies(
      strategies
        .filter((s) => !s.isArchived || new Date(s.endDate).getFullYear() >= Math.min(...filterYears))
        .reduce((acc, s) => ({ ...acc, [s.id]: `${s.num} - ${s.name}` }), {}),
  )};

  useEffect(async () => {
    const strategies = await api.get('/mng/strategies');
    if (strategies && !strategies.error) {
      setStrategies(strategies)
    }

    const respProjResults = await api.get('/reports/strategies/0/projresults');
    setData(mapDtoToTable(respProjResults));
  }, []);

  const toggleExpand = () => {
    setData((prev) => prev.map((d) => ({ ...d, tableData: { isTreeExpanded: !allRowsExpanded } })));
    setAllRowsExpanded((prev) => !prev);
  };

  const getColumns = () => {
    const colsStart = [
      {
        title: 'Мероприятие Постановления №729',
        field: 'id',
        defaultSort: 'asc',
        filterCellStyle: { maxWidth: '300px', width: '4%' },
        render: (rowData) => (!rowData.parentId
          ? (<TooltippedElement tooltip={rowData.descr}><span style={{ fontSize: '16px' }}>{rowData.name}</span></TooltippedElement>)
          : ''),
        customFilterAndSearch: (term, rowData) => term.length === 0
          || (!rowData.parentId && term.toLowerCase().indexOf(rowData.name.toLowerCase()) >= 0)
          || (rowData.parentId && term.toLowerCase().indexOf(rowData.parentId.toLowerCase()) >= 0),
      },
      {
        title: 'Стратегический проект',
        field: 'strategyId',
        filterCellStyle: { minWidth: '300px', width: '26%', maxWidth: '300px' },
        width: '35%',
        render: (rowData) => (rowData.parentId ? `${rowData.num} - ${rowData.name}` : ''),
        lookup: filteredStrategies,
        customFilterAndSearch: (term, rowData) => term.length === 0
          || (rowData.strategyId && term.includes(rowData.strategyId.toString())),
      },
    ];

    const colsMiddle = getYearRelatedColumns(filterYears, filterFunding);

    return [...colsStart, ...colsMiddle];
  };

  useEffect(() => {
    updateStrategies(strategies)
  }, [filterYears, strategies])

  useEffect(() => {
    setFilteredData(data
      .filter(item => !item.strategyId  || !!filteredStrategies[item.strategyId])
      .filter((item, _, filteredChilds) => item.strategyId || filteredChilds.find(childItem => item.id === childItem.parentId)))
  }, [data, filteredStrategies])

  return (
    <section className="page p2p-page">
      <div style={styledTable}>
        <MaterialTable
          options={modifyDefaultOptions({
            // tableLayout: 'fixed',
            paging: false,
            draggable: false,
            rowStyle: (rowData) => ({
              backgroundColor: !rowData.parentId ? '#FFF' : '#F7F7FF',
            }),
            maxBodyHeight: 'calc(100vh - 442px)',
            headerStyle: { height: 102, fontSize: '13px' },
            filterCellStyle: {
              position: 'sticky',
              top: 159,
              background: 'white',
              zIndex: 5,
            },
          })}
          icons={tableIcons}
          localization={localization}
          columns={getColumns()}
          data={filteredData}
          title={(
            <>
              <h3 style={styledTableHeading}>Детализация показателя ПРГ2</h3>
              Общее количество реализованных проектов, в том числе с участием членов консорциума (консорциумов), по каждому из мероприятий программ развития, указанных в пункте 5 Правил проведения отбора, ед.
            </>
)}
          parentChildData={(row, rows) =>
            rows.find((a) => a.id === row.parentId)}
          components={{
            Header: (props) => (<MTableHeader {...props} tableHeadStyle={styledHeaderSticky}>{getHeader(filterYears, filterFunding)}</MTableHeader>),
            FilterRow: (props) => (<MTableFilterRow {...props} />),
            Toolbar: (nativeProps) => (
              <ToolbarWrapper
                nativeProps={nativeProps}
                customToolbarElement={(
                  <Button style={{ flex: 'none', marginLeft: 16 }} onClick={toggleExpand}>
                    {allRowsExpanded ? 'Свернуть' : 'Развернуть'}
                    {' '}
                    все
                  </Button>
              )}
                filters={{
                  yearsProps: { selectedYears: filterYears, onChange: setFilterYears },
                  fundingProps: { selectedFundings: filterFunding, onChange: setFilterFunding },
                }}
              />
            ),
            Body: (props) => (
              <>
                <MTableBody {...props} />
                { getFooter(props, filterYears, filterFunding) }
              </>
            ),
          }}
        />
      </div>
    </section>
  );
}

const styledTable = {
  maxWidth: '100%',
  marginTop: 32,
};

const styledHeaderSticky = { position: 'sticky', top: 0, zIndex: 10 };
const styledToolbarWrapper = { marginBottom: '16px' };
const styledToolbarContainer = { display: 'flex', alignItems: 'center', justifyContent: 'space-between' };
